import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtMoney } from "@/lib/api";
import { PageTitle, SectionCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Settings2, RefreshCw, Save, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const now = new Date();
const SCHEME_LABEL = { flat_percent: "Flat %", tiered_percent: "Tiered %", fixed_per_deal: "Fixed / deal" };

function calc(scheme, sales, deals) {
  if (!scheme) return 0;
  sales = parseFloat(sales) || 0; deals = parseInt(deals) || 0;
  if (scheme.type === "flat_percent") return Math.round(sales * (scheme.flat_percent || 0)) / 100;
  if (scheme.type === "fixed_per_deal") return Math.round(deals * (scheme.fixed_per_deal || 0) * 100) / 100;
  if (scheme.type === "tiered_percent") {
    const tiers = [...(scheme.tiers || [])].sort((a, b) => (a.min || 0) - (b.min || 0));
    let pct = 0; tiers.forEach((t) => { if (sales >= (t.min || 0)) pct = t.percent || 0; });
    return Math.round(sales * pct) / 100;
  }
  return 0;
}

function schemeSummary(s) {
  if (!s) return "";
  if (s.type === "flat_percent") return `${s.flat_percent || 0}% of sales`;
  if (s.type === "fixed_per_deal") return `$${s.fixed_per_deal || 0} per deal`;
  if (s.type === "tiered_percent") return (s.tiers || []).map((t) => `≥$${t.min}: ${t.percent}%`).join(", ");
  return "";
}

export default function Commissions() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({}); // employee_id -> {sales_value, deal_count, amount}
  const [syncing, setSyncing] = useState(false);
  const period = `${year}-${String(month).padStart(2, "0")}`;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/commissions/period/${period}`);
      setData(r.data);
      const map = {};
      r.data.rows.forEach((row) => {
        const e = row.entry;
        map[row.employee_id] = e
          ? { sales_value: e.sales_value, deal_count: e.deal_count, amount: e.amount, bonus: e.bonus ?? "" }
          : { sales_value: "", deal_count: "", amount: "", bonus: "" };
      });
      setRows(map);
    } catch (e) { toast.error(apiErr(e)); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const setRow = (id, patch, scheme) => {
    setRows((prev) => {
      const cur = { ...(prev[id] || {}), ...patch };
      if ("sales_value" in patch || "deal_count" in patch) {
        cur.amount = calc(scheme, cur.sales_value, cur.deal_count); // live recompute
      }
      return { ...prev, [id]: cur };
    });
  };

  const saveRow = async (row) => {
    const r = rows[row.employee_id] || {};
    try {
      await api.post("/commissions", {
        employee_id: row.employee_id, period,
        sales_value: parseFloat(r.sales_value) || 0,
        deal_count: parseInt(r.deal_count) || 0,
        amount: r.amount === "" || r.amount == null ? null : parseFloat(r.amount),
        bonus: parseFloat(r.bonus) || 0,
      });
      toast.success(`Saved for ${row.full_name}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const clearRow = async (row) => {
    if (!row.entry) { setRow(row.employee_id, { sales_value: "", deal_count: "", amount: "", bonus: "" }); return; }
    try { await api.delete(`/commissions/${row.entry.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const syncCrm = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/commissions/sync-crm", { year, month });
      toast[r.data.synced ? "success" : "info"](r.data.message);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSyncing(false); }
  };

  const approve = async (id) => {
    try { await api.post(`/commissions/${id}/approve`); toast.success("Commission approved"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const approveAll = async () => {
    try { const r = await api.post("/commissions/approve-all", { period }); toast.success(`Approved ${r.data.approved} commission(s)`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const pending = (data?.rows || []).filter((r) => r.entry && r.entry.status === "pending").length;

  const onSchemeSaved = (id, scheme) => {
    setData((d) => ({ ...d, rows: d.rows.map((x) => (x.employee_id === id ? { ...x, scheme } : x)) }));
    setRows((prev) => {
      const cur = prev[id] || {};
      const touched = cur.sales_value !== "" || cur.deal_count !== "";
      return touched ? { ...prev, [id]: { ...cur, amount: calc(scheme, cur.sales_value, cur.deal_count) } } : prev;
    });
  };

  const total = Object.values(rows).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const totalBonus = Object.values(rows).reduce((s, r) => s + (parseFloat(r.bonus) || 0), 0);
  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Sales Commissions & Bonuses" title="Commissions & Bonuses">
        <Select value={String(month)} onValueChange={(v) => setMonth(+v)}>
          <SelectTrigger className="w-28" data-testid="comm-month"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(+v)}>
          <SelectTrigger className="w-24" data-testid="comm-year"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={syncCrm} disabled={syncing} data-testid="comm-sync-crm"><RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> Sync from CRM</Button>
        {pending > 0 && <Button onClick={approveAll} data-testid="comm-approve-all"><Check className="h-4 w-4 mr-1.5" /> Approve all ({pending})</Button>}
        {data && <SettingsDialog config={data} onSaved={load} />}
      </PageTitle>

      {data && !data.crm.enabled && (
        <div className="mb-4 text-xs text-muted-foreground bg-muted/40 border border-border rounded-md px-3 py-2" data-testid="comm-crm-note">
          Auto-sync from LeadifyCRM is <b>inactive</b>. Add its URL &amp; API key and enable it in <b>Commission Settings</b>. Until then, enter commissions manually below. Each staff uses their own commission scheme (click the scheme badge to set it). Bonuses are not subject to CPF or SDL. Both flow into that month's payslips automatically.
        </div>
      )}

      <SectionCard title={`Commissions & Bonuses for ${MONTHS[month - 1]} ${year}`}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Scheme</TableHead>
              <TableHead className="text-right">Sales Value ($)</TableHead>
              <TableHead className="text-right">Deals</TableHead>
              <TableHead className="text-right">Commission ($)</TableHead>
              <TableHead className="text-right">Bonus ($)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.rows || []).map((row) => {
              const r = rows[row.employee_id] || {};
              return (
                <TableRow key={row.employee_id} data-testid={`comm-row-${row.employee_id}`}>
                  <TableCell><div className="font-medium">{row.full_name}</div><div className="text-xs text-muted-foreground">{row.role}</div></TableCell>
                  <TableCell><EmpSchemeDialog row={row} onSaved={onSchemeSaved} /></TableCell>
                  <TableCell className="text-right"><Input type="number" value={r.sales_value} onChange={(e) => setRow(row.employee_id, { sales_value: e.target.value }, row.scheme)} className="w-32 ml-auto text-right" data-testid={`comm-sales-${row.employee_id}`} /></TableCell>
                  <TableCell className="text-right"><Input type="number" value={r.deal_count} onChange={(e) => setRow(row.employee_id, { deal_count: e.target.value }, row.scheme)} className="w-20 ml-auto text-right" data-testid={`comm-deals-${row.employee_id}`} /></TableCell>
                  <TableCell className="text-right"><Input type="number" value={r.amount} onChange={(e) => setRow(row.employee_id, { amount: e.target.value })} className="w-28 ml-auto text-right font-semibold" data-testid={`comm-amount-${row.employee_id}`} /></TableCell>
                  <TableCell className="text-right"><Input type="number" value={r.bonus} onChange={(e) => setRow(row.employee_id, { bonus: e.target.value })} className="w-28 ml-auto text-right" placeholder="0.00" data-testid={`comm-bonus-${row.employee_id}`} /></TableCell>
                  <TableCell>
                    {row.entry ? (row.entry.status === "pending"
                      ? <Badge variant="outline" className="text-amber-600 border-amber-400" data-testid={`comm-status-${row.employee_id}`}>Pending</Badge>
                      : <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid={`comm-status-${row.employee_id}`}>Approved</Badge>)
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {row.entry && row.entry.status === "pending" && <Button size="sm" variant="outline" className="mr-1 text-emerald-600 border-emerald-400" onClick={() => approve(row.entry.id)} data-testid={`comm-approve-${row.employee_id}`}><Check className="h-3.5 w-3.5" /></Button>}
                    <Button size="sm" variant="outline" onClick={() => saveRow(row)} data-testid={`comm-save-${row.employee_id}`}><Save className="h-3.5 w-3.5" /></Button>
                    {row.entry && <Button size="sm" variant="ghost" className="ml-1 text-destructive" onClick={() => clearRow(row)} data-testid={`comm-clear-${row.employee_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <div className="flex justify-end gap-6 mt-4 text-sm font-semibold" data-testid="comm-total"><span>Total commissions: {fmtMoney(total)}</span><span data-testid="bonus-total">Total bonuses: {fmtMoney(totalBonus)}</span></div>
      </SectionCard>
    </div>
  );
}

const NEW_SCHEME = { type: "flat_percent", flat_percent: 5, tiers: [{ min: 0, percent: 3 }, { min: 10000, percent: 5 }], fixed_per_deal: 100 };

function SchemeFields({ scheme, setScheme }) {
  const setTier = (i, patch) => setScheme((s) => ({ ...s, tiers: s.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) }));
  const addTier = () => setScheme((s) => ({ ...s, tiers: [...(s.tiers || []), { min: 0, percent: 0 }] }));
  const delTier = (i) => setScheme((s) => ({ ...s, tiers: s.tiers.filter((_, j) => j !== i) }));
  return (
        <div className="space-y-5">
          <div>
            <Label>Commission Scheme</Label>
            <Select value={scheme.type} onValueChange={(v) => setScheme({ ...scheme, type: v })}>
              <SelectTrigger className="mt-1.5" data-testid="comm-scheme-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="flat_percent">Flat % of sales</SelectItem>
                <SelectItem value="tiered_percent">Tiered % (by monthly sales)</SelectItem>
                <SelectItem value="fixed_per_deal">Fixed amount per deal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scheme.type === "flat_percent" && (
            <div><Label>Percentage of sales value (%)</Label><Input type="number" value={scheme.flat_percent} onChange={(e) => setScheme({ ...scheme, flat_percent: parseFloat(e.target.value) || 0 })} className="mt-1.5" data-testid="comm-flat" /></div>
          )}
          {scheme.type === "fixed_per_deal" && (
            <div><Label>Amount per completed deal ($)</Label><Input type="number" value={scheme.fixed_per_deal} onChange={(e) => setScheme({ ...scheme, fixed_per_deal: parseFloat(e.target.value) || 0 })} className="mt-1.5" data-testid="comm-fixed" /></div>
          )}
          {scheme.type === "tiered_percent" && (
            <div className="space-y-2">
              <Label>Tiers — % applied to total monthly sales at/above each threshold</Label>
              {(scheme.tiers || []).map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">≥ $</span>
                  <Input type="number" value={t.min} onChange={(e) => setTier(i, { min: parseFloat(e.target.value) || 0 })} className="w-32" />
                  <span className="text-xs text-muted-foreground">→</span>
                  <Input type="number" value={t.percent} onChange={(e) => setTier(i, { percent: parseFloat(e.target.value) || 0 })} className="w-24" />
                  <span className="text-xs text-muted-foreground">%</span>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => delTier(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addTier} data-testid="comm-add-tier"><Plus className="h-4 w-4 mr-1" /> Add tier</Button>
            </div>
          )}
        </div>
  );
}

function EmpSchemeDialog({ row, onSaved }) {
  const [open, setOpen] = useState(false);
  const [scheme, setScheme] = useState(row.scheme || NEW_SCHEME);
  useEffect(() => { if (open) { setScheme(row.scheme || NEW_SCHEME); } }, [open, row]);
  const save = async (val) => {
    try {
      await api.post("/commissions/employee-scheme", { employee_id: row.employee_id, scheme: val });
      toast.success(val ? `Scheme saved for ${row.full_name}` : `Scheme removed for ${row.full_name}`);
      setOpen(false); onSaved(row.employee_id, val);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid={`comm-scheme-btn-${row.employee_id}`} title={row.scheme ? schemeSummary(row.scheme) : "Set this staff's commission scheme"}>
          {row.scheme
            ? <Badge variant="secondary" className="cursor-pointer">{SCHEME_LABEL[row.scheme.type]} · {schemeSummary(row.scheme)}</Badge>
            : <Badge variant="outline" className="cursor-pointer text-amber-600 border-amber-400">No scheme — set</Badge>}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Commission scheme · {row.full_name}</DialogTitle></DialogHeader>
        <SchemeFields scheme={scheme} setScheme={setScheme} />
        <DialogFooter className="gap-2">
          {row.scheme && <Button variant="outline" className="text-destructive" onClick={() => save(null)} data-testid="emp-scheme-remove">Remove scheme</Button>}
          <Button onClick={() => save(scheme)} data-testid="emp-scheme-save">Save Scheme</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ config, onSaved }) {
  const [open, setOpen] = useState(false);
  const [crmUrl, setCrmUrl] = useState(config.crm.url || "");
  const [crmKey, setCrmKey] = useState("");
  const [crmEnabled, setCrmEnabled] = useState(!!config.crm.enabled);

  useEffect(() => { if (open) { setCrmUrl(config.crm.url || ""); setCrmKey(""); setCrmEnabled(!!config.crm.enabled); } }, [open, config]);

  const save = async () => {
    try {
      await api.put("/commissions/config", {
        crm_url: crmUrl,
        crm_api_key: crmKey === "" ? null : crmKey, // null = keep existing
        crm_enabled: crmEnabled,
      });
      toast.success("Commission settings saved");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="comm-settings-btn"><Settings2 className="h-4 w-4 mr-1.5" /> Settings</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Commission Settings</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="space-y-3">
            <Label className="flex items-center justify-between">LeadifyCRM Auto-Sync
              <label className="flex items-center gap-2 text-xs font-normal cursor-pointer">
                <input type="checkbox" checked={crmEnabled} onChange={(e) => setCrmEnabled(e.target.checked)} data-testid="comm-crm-enabled" /> Enabled
              </label>
            </Label>
            <div><Label className="text-xs text-muted-foreground">CRM Base URL</Label><Input value={crmUrl} onChange={(e) => setCrmUrl(e.target.value)} placeholder="https://leadifycrm-xxxx.preview.emergentagent.com" className="mt-1" data-testid="comm-crm-url" /></div>
            <div><Label className="text-xs text-muted-foreground">CRM API Key</Label><Input type="password" value={crmKey} onChange={(e) => setCrmKey(e.target.value)} placeholder={config.crm.has_key ? "•••••• (unchanged)" : "Paste API key"} className="mt-1" data-testid="comm-crm-key" /></div>
            <p className="text-xs text-muted-foreground">The CRM must expose <code>GET /api/deals/completed-won?from&amp;to</code> returning <code>[{"{ owner_name, deal_value, completed_date }"}]</code> for fully Closed-Won &amp; completed projects.</p>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="comm-settings-save">Save Settings</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
